import { BlockModule } from '@/website/blocks/common'
import gsap from 'gsap'

new BlockModule({
    selector: '.block-form-bn-hero-banner-block',

    setupBlock(el, onTeardown, context) {
        // Elements
        const formContainer = el.querySelector('#form-container') as HTMLElement
        const clickEffect = el.querySelector('#click-effect') as HTMLElement
        const submitBtn = el.querySelector('#submit') as HTMLButtonElement
        const successMsg = el.querySelector('#success') as HTMLElement

        const nameInput = el.querySelector('#name') as HTMLInputElement
        const emailInput = el.querySelector('#email') as HTMLInputElement
        const passwordInput = el.querySelector('#password') as HTMLInputElement
        const emailError = el.querySelector('#email-error') as HTMLElement

        const check1 = el.querySelector('#check1')
        const check2 = el.querySelector('#check2')
        const check3 = el.querySelector('#check3')

        // Text to type
        const nameText = 'Yusof Abdullah'
        const invalidEmailText = 'example.gov.bn'
        const validEmailText = 'yusof@example.gov.bn'
        const passwordText = '••••••••••'

        // Create a GSAP timeline
        let mainTl: GSAPTimeline | null = null

        // Function to get updated element positions
        function getElementPositions() {
            // Calculate proper positions
            const formRect = formContainer?.getBoundingClientRect()
            const nameRect = nameInput?.getBoundingClientRect()
            const emailRect = emailInput?.getBoundingClientRect()
            const passwordRect = passwordInput?.getBoundingClientRect()
            const submitRect = submitBtn?.getBoundingClientRect()

            if (!formRect || !nameRect || !emailRect || !passwordRect || !submitRect) {
                return
            }

            return {
                formCenter: {
                    x: formRect.left + formRect.width / 2,
                    y: formRect.top + formRect.height / 2,
                },
                name: {
                    x: nameRect.left + 15,
                    y: nameRect.top + nameRect.height / 2,
                },
                email: {
                    x: emailRect.left + 15,
                    y: emailRect.top + emailRect.height / 2,
                },
                password: {
                    x: passwordRect.left + 15,
                    y: passwordRect.top + passwordRect.height / 2,
                },
                submit: {
                    x: submitRect.left + submitRect.width / 2,
                    y: submitRect.top + submitRect.height / 2,
                },
            }
        }

        // Start the animation
        function initAnimation() {
            // Get positions
            const positions = getElementPositions()
            startAnimation(positions)
        }

        // Wait for layout to settle
        setTimeout(initAnimation, 100)

        function startAnimation(pos: ReturnType<typeof getElementPositions>) {
            // Reset everything
            resetForm()

            // Create a new timeline
            mainTl = gsap.timeline({
                onComplete: () => {
                    // Restart animation after delay
                    setTimeout(() => initAnimation(), 3000)
                },
            })

            // ================================
            // 1. INITIAL STATE
            // ================================

            // ================================
            // 2. NAME FIELD SECTION
            // ================================
            // View zooms in to the name field
            mainTl.to(formContainer, {
                scale: 1.2,
                transformOrigin: '50% 20%',
                duration: 0.7,
                ease: 'power1.inOut',
            })

            // Type in the name
            mainTl.add(typeText(nameInput, nameText))

            // Show completion
            mainTl.to(check1, { opacity: 1, duration: 0.3 })
            mainTl.to(
                nameInput,
                {
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    duration: 0.3,
                },
                '<',
            )

            // Add a pause before moving to next field
            mainTl.to({}, { duration: 1.5 })

            // ================================
            // 3. EMAIL FIELD SECTION
            // ================================
            // Move to email with updated positions and pan form
            mainTl.add(function () {
                const emailTl = gsap.timeline()

                // Then pan the form
                emailTl.to(
                    formContainer,
                    {
                        y: -50,
                        duration: 0.7,
                        ease: 'power1.inOut',
                    },
                    '-=0.3',
                ) // Slight overlap

                return emailTl
            })

            // Wait a moment before typing
            mainTl.to({}, { duration: 0.2 })

            // Type invalid email
            mainTl.add(function () {
                return typeText(emailInput, invalidEmailText)
            })

            // Show error
            mainTl.to(emailInput, {
                borderColor: '#ef4444',
                duration: 0.3,
            })

            mainTl.to(
                emailError,
                {
                    opacity: 1,
                    duration: 0.3,
                },
                '<',
            )

            // Pause to show error
            mainTl.to({}, { duration: 2.4 })

            // Delete invalid email
            mainTl.add(function () {
                const deleteTl = gsap.timeline()

                // Delete characters one by one
                for (let i = invalidEmailText.length; i > 0; i--) {
                    deleteTl.add(
                        function () {
                            emailInput.value = invalidEmailText.substring(0, i - 1)
                        },
                        (invalidEmailText.length - i) * 0.05,
                    )
                }

                return deleteTl
            })

            // Reset error state
            mainTl.to(emailError, {
                opacity: 0,
                duration: 0.3,
            })

            mainTl.to(
                emailInput,
                {
                    borderColor: '#d1d5db',
                    duration: 0.3,
                },
                '<',
            )

            // Pause before typing correct email
            mainTl.to({}, { duration: 1 })

            // Type correct email
            mainTl.add(function () {
                return typeText(emailInput, validEmailText)
            })

            // Pause before showing completion
            mainTl.to({}, { duration: 0.5 })

            // Show completion
            mainTl.to(check2, { opacity: 1, duration: 0.3 })
            mainTl.to(
                emailInput,
                {
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    duration: 0.3,
                },
                '<',
            )

            // Pause before moving to password
            mainTl.to({}, { duration: 1.4 })

            // ================================
            // 4. PASSWORD FIELD SECTION
            // ================================
            // Move to password with updated positions
            mainTl.add(function () {
                const passwordTl = gsap.timeline()

                // Then pan the form
                passwordTl.to(
                    formContainer,
                    {
                        y: -100,
                        duration: 0.7,
                        ease: 'power1.inOut',
                    },
                    '-=0.3',
                ) // Slight overlap

                return passwordTl
            })

            // Wait a moment before typing
            mainTl.to({}, { duration: 0.2 })

            // Type password
            mainTl.add(function () {
                return typeText(passwordInput, passwordText)
            })

            // Show completion
            mainTl.to(check3, { opacity: 1, duration: 0.3 })
            mainTl.to(
                passwordInput,
                {
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    duration: 0.3,
                },
                '<',
            )

            // Pause before moving to submit
            mainTl.to({}, { duration: 1.5 })

            // ================================
            // 5. SUBMIT BUTTON SECTION
            // ================================

            // Change button color
            mainTl.to(submitBtn, {
                backgroundColor: '#10b981',
                color: 'white',
                duration: 0.3,
            })

            // ================================
            // 6. ZOOM OUT AND SHOW SUCCESS
            // ================================
            // Zoom out
            mainTl.to(formContainer, {
                scale: 1,
                y: 0,
                duration: 0.7,
                ease: 'power1.inOut',
            })

            // Show success message
            mainTl.to(successMsg, {
                opacity: 1,
                duration: 0.5,
            })
        }

        function resetForm() {
            // Kill any existing animations
            if (mainTl) {
                mainTl.kill()
            }

            // Reset GSAP properties
            gsap.set(clickEffect, { opacity: 0, scale: 0 })
            gsap.set([check1, check2, check3, successMsg, emailError], {
                opacity: 0,
            })
            gsap.set([nameInput, emailInput, passwordInput], {
                borderColor: '#d1d5db',
                backgroundColor: 'white',
            })
            gsap.set(submitBtn, { backgroundColor: 'hsl(42 100% 64%)', color: 'hsl(214 84% 41%)' })
            gsap.set(formContainer, { scale: 1, x: 0, y: 0 })

            // Clear input values
            nameInput.value = ''
            emailInput.value = ''
            passwordInput.value = ''
        }

        function typeText(inputElement: HTMLInputElement, text: string) {
            const tl = gsap.timeline()

            let currentText = ''

            // For each character in the text
            for (let i = 0; i < text.length; i++) {
                tl.add(() => {
                    currentText += text.charAt(i)
                    inputElement.value = currentText
                }, i * 0.1)
            }

            return tl
        }

        onTeardown(() => {
            mainTl?.kill()
        })
    },
})
